import { useEffect, useRef, useState } from 'react';
import { DashboardEventNames } from 'logi-embed';
import { publishFilters, renderLogiComponent } from '../logiEmbedManager';
import { buildLogiDashboardComponentConfig, formatDateRangeForLogiFiltering, } from '../logiUtils';
const useRenderComponent = (
// The configuration for the Logi component to be rendered
componentConfig, 
// The ID of the HTML element where the component will be rendered
componentElementId, 
// The date range from Redux
dateRange, 
// The current environment from window.cmdContainer
environment, 
// The current orgId
orgId) => {
    const isFirstRender = useRef(true);
    const [componentError, setComponentError] = useState(null);
    const [componentLoading, setComponentLoading] = useState(true);
    const onError = (error) => {
        setComponentError(error);
        setComponentLoading(false);
        // eslint-disable-next-line no-console
        console.error('Error rendering Logi component:', error);
    };
    useEffect(() => {
        // Per Logi docs, the composer-dashboard-ready DOM event must be listened for BEFORE
        // attempting to publish cross-visual filters. Set up the promise first, then render.
        // This is a work-around since the initialFilter logic isn't friendly at the moment.
        const DASHBOARD_READY_EVENT = 'composer-dashboard-ready';
        const pubSubReady = new Promise((resolve) => {
            const onReady = () => {
                resolve();
                document.removeEventListener(DASHBOARD_READY_EVENT, onReady);
            };
            document.addEventListener(DASHBOARD_READY_EVENT, onReady);
        });
        try {
            const dashboardConfig = buildLogiDashboardComponentConfig(componentConfig, dateRange, environment);
            const componentLoaded = new Promise((resolve) => {
                renderLogiComponent(dashboardConfig, componentElementId, orgId).then((component) => {
                    component.addEventListener(DashboardEventNames.LOADED, () => resolve());
                }).catch(onError);
            });
            // Drop the loading mask only after both the dashboard has loaded AND pub/sub is ready,
            // so the user never sees a flash of unfiltered data before initial date filters are applied
            Promise.all([componentLoaded, pubSubReady]).then(() => {
                setComponentLoading(false);
            });
        }
        catch (error) {
            onError(error);
        }
        // Publish "initial" date filters once Logi's pub/sub system signals it is ready
        pubSubReady.then(() => {
            const fieldsToPublish = componentConfig.dateFields?.length
                ? componentConfig.dateFields
                : componentConfig.dateField ? [componentConfig.dateField] : [];
            if (fieldsToPublish.length > 0) {
                const publishObject = {
                    type: 'selection',
                    valueType: 'TIME',
                    ranges: [{ operation: 'BETWEEN', value: formatDateRangeForLogiFiltering(dateRange) }],
                };
                fieldsToPublish.forEach((field) => publishFilters(field, publishObject, orgId));
            }
        });
    }, []);
    useEffect(() => {
        // Prevent publishing on initial render - we only want to publish when the date changes,
        // not when the component first loads
        // Initial date filters will be set by the initialFilters property of the component config
        // PROBLEM: if the new range is outside the range defined in initialFilters, the dashboard will show
        // "no data" because I guess this new published filter is being ANDed with the initial filter,
        // but the initial filter is never updated to reflect the new date range. And I thought it worked...
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        // Only publish if date fields are defined in the config, otherwise we don't know what field to publish on
        const fieldsToPublish = componentConfig.dateFields?.length
            ? componentConfig.dateFields
            : componentConfig.dateField ? [componentConfig.dateField] : [];
        if (fieldsToPublish.length > 0) {
            const publishObject = {
                type: 'selection',
                valueType: 'TIME',
                ranges: [
                    {
                        operation: 'BETWEEN',
                        value: formatDateRangeForLogiFiltering(dateRange),
                    },
                ],
            };
            // Need to publish a filter for each date field in the config (i.e there can be
            // multiple date fields for a dashboard if multiple data sources are used
            // in that dashboard)
            fieldsToPublish.forEach((field) => publishFilters(field, publishObject, orgId));
        }
    }, [dateRange]);
    return {
        componentLoading,
        componentError,
    };
};
export default useRenderComponent;
